import React, { useState, useEffect, createRef } from 'react'
import { node, func, bool, string, array } from 'prop-types'
import styled from 'styled-components'
import { readableColor } from 'polished'

import { Portal } from '../Portal'
import { CloseCircle } from '../../icons/CloseCircle'
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick'

export { ModalBanner } from './ModalBanner'

const ModalContainer = styled.div`
  z-index: 9999;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: ${({ theme }) => theme.modalOverlay};
  ${({ modalStyles }) => modalStyles};
`
const ModalContent = styled.div`
  margin-top: ${({ noMargin }) => (noMargin ? '0' : '8rem')};
  position: relative;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => readableColor(theme.backgroundColor)};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 3px;
  background: ${({ theme }) => theme.backgroundAccent};
  ${({ theme }) => theme.cardShadow};

  @media ${({ theme }) => `(max-width: ${theme.breakpoints[0]})`} {
    width: 100vw;
    min-height: 100vh;
  }
`

const ModalBody = styled.div`
  flex: 1;
  color: ${({ theme }) => readableColor(theme.backgroundColor)};
  padding: 14px 29px 14px 29px;
`
const ModalHeader = styled.div`
  padding: 9px 10px;
  padding-left: 20px;
  min-height: 20px;
  position: relative;
  ${({ title, theme }) =>
    title &&
    `
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 25px;
    background: ${theme.cardTop || theme.backgroundColor};
  `}
`

const CloseButton = styled.button`
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  position: absolute;
  right: 10px;
  cursor: pointer;
  ${({ disabled }) => disabled && '& > svg{opacity: 0.5;}'}

  ${({ fullscreen }) => fullscreen && 'top: 30px; right: 30px'}
`

export const Modal = ({
  setIsVisible,
  children,
  modalRoot,
  skipOutsideClick,
  locked,
  scrollLock,
  fullscreen,
  title,
  noMargin,
  modalStyles,
  ...rest
}) => {
  const handleClose = () => setIsVisible(false)
  const [focusTrapped, setFocusTrapped] = useState(false)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    function keyListener(e) {
      const listener = keyListenersMap.get(e.key)
      return listener && listener(e)
    }

    document.addEventListener('keydown', keyListener)

    return () => document.removeEventListener('keydown', keyListener)
  })

  const modalRef = createRef()

  useOnOutsideClick(modalRef, handleClose, skipOutsideClick)

  const handleTabKey = (e) => {
    const focusableModalElements = modalRef.current.querySelectorAll(
      'a[href], button, textarea,   input[type="text"], input[type="radio"], input[type="checkbox"], select'
    )
    const firstElement = focusableModalElements[0]
    const lastElement = focusableModalElements[focusableModalElements.length - 1]

    if (!focusTrapped) {
      if (Array.from(focusableModalElements).find((e) => e === document.activeElement)) {
        setFocusTrapped(true)
      } else {
        firstElement.focus()
        setFocusTrapped(true)
      }
    }

    if (focusableModalElements.length === 1) {
      firstElement.focus()
      return e.preventDefault()
    }

    if (!e.shiftKey && document.activeElement === lastElement) {
      firstElement.focus()
      return e.preventDefault()
    }

    if (e.shiftKey && document.activeElement === firstElement) {
      lastElement.focus()
      e.preventDefault()
    }
  }

  const keyListenersMap = new Map([
    ['Escape', () => setIsVisible(false)],
    ['Tab', handleTabKey],
  ])

  return (
    <Portal default={modalRoot} scrollLock={scrollLock}>
      <ModalContainer role='dialog' aria-modal='true' modalStyles={modalStyles}>
        <ModalContent {...rest} id='modal-content' noMargin={noMargin} ref={modalRef}>
          <ModalHeader title={title}>
            {title && <span>{title}</span>}
            <CloseButton
              title='close modal'
              onClick={handleClose}
              disabled={locked}
              fullscreen={fullscreen}
            >
              <CloseCircle
                title='close'
                height={25}
                width={25}
                fill={({ theme }) => readableColor(theme.backgroundColor)}
                stroke={({ theme }) => readableColor(theme.backgroundColor)}
              />
            </CloseButton>
          </ModalHeader>
          <ModalBody>{children}</ModalBody>
        </ModalContent>
      </ModalContainer>
    </Portal>
  )
}

Modal.propTypes = {
  setIsVisible: func,
  children: node,
  modalRoot: bool,
  skipOutsideClick: bool,
  locked: bool,
  scrollLock: bool,
  fullscreen: bool,
  title: string,
  noMargin: bool,
  modalStyles: array,
}

Modal.defaultProps = {
  skipOutsideClick: false,
  locked: false,
  scrollLock: false,
  fullscreen: false,
  noMargin: false,
}
